import type { ProductDetailAttribute } from '~/types/enrichment';

/**
 * Parses an enriched `product-details` optimization value into structured
 * attributes. Handles the two JSON array shapes (new snake_case, old
 * camelCase) and the legacy pipe-delimited string, same three formats
 * `enriched-attribute-row.tsx`'s (unexported) `parseEnrichedAttrs` and
 * `feed-audit`'s `parseEnrichedProductDetails` both already parse.
 *
 * Deliberately duplicated rather than imported: the enriched-attribute-row
 * copy is a private, unexported function local to a component, and importing
 * feed-audit's copy here would point a dependency the wrong way (feed-audit
 * already depends on catalog-operations, not the reverse). If a fourth
 * surface ever needs this, promote all three to one shared utility instead of
 * adding a fourth copy.
 */
export function parseEnrichedProductDetailAttrs(value: string): ProductDetailAttribute[] {
  try {
    const parsed = JSON.parse(value) as unknown;
    if (Array.isArray(parsed) && parsed.length > 0) {
      const first = parsed[0] as Record<string, unknown>;
      if ('section_name' in first) {
        return (
          parsed as { section_name: string; attribute_name: string; attribute_value: string }[]
        ).map((item) => ({
          sectionName: item.section_name,
          attributeName: item.attribute_name,
          attributeValue: item.attribute_value,
        }));
      }
      if ('sectionName' in first) {
        return (
          parsed as { sectionName: string; attributeName: string; attributeValue: string }[]
        ).map((item) => ({
          sectionName: item.sectionName,
          attributeName: item.attributeName,
          attributeValue: item.attributeValue,
        }));
      }
    }
  } catch {
    // Not JSON — fall through to legacy pipe-delimited parsing below.
  }

  return value
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((segment) => {
      const parts = segment.split(': ');
      if (parts.length < 3) return null;
      const [sectionName, attributeName, ...rest] = parts as [string, string, ...string[]];
      return {
        sectionName,
        attributeName,
        attributeValue: rest.join(': '),
      } satisfies ProductDetailAttribute;
    })
    .filter((a): a is ProductDetailAttribute => a !== null);
}

/**
 * Formats parsed product-detail attributes the way the GMC feed export
 * encodes them (the "Attribute triples" rule): each triple colon-joined with
 * no spaces (`section:attribute:value`), entries joined with `, ` (comma +
 * space, for readability in Preview) — Google's published Merchant Center
 * `product_detail` feed format. Empty string for no attributes.
 */
export function formatProductDetailAttrsForFeed(attrs: ProductDetailAttribute[]): string {
  return attrs
    .map(({ sectionName, attributeName, attributeValue }) =>
      [sectionName, attributeName, attributeValue].join(':'),
    )
    .join(', ');
}
